import { Injectable, Type } from '@angular/core';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { map } from 'rxjs/operators';

export type SidePanelContent<T> = {
  component: Type<T>;
  data?: any;
  onClose$: Subject<any>;
};

@Injectable({
  providedIn: 'root'
})
export class SidePanelService {
  private readonly componentStack$ = new BehaviorSubject<SidePanelContent<any>[]>([]);

  public readonly currentContent$: Observable<SidePanelContent<any> | null> =
    new Observable(subscriber => {
      this.componentStack$.subscribe(stack => {
        subscriber.next(stack.length > 0 ? stack[stack.length - 1] : null);
      });
    });

  /**
   * Opens a new component in the side panel. This is the primary entry point.
   * @param component The component class to render.
   * @param data Optional data to pass to the component.
   * @returns An observable that emits when the panel is closed via `close()` or completes on `dismiss()`.
   */
  public open<T>(component: Type<T>, data?: any): Observable<any> {
    const onClose$ = new Subject<any>();

    const newContent: SidePanelContent<T> = {
      component,
      data,
      onClose$,
    };

    const currentStack = this.componentStack$.getValue();
    this.componentStack$.next([...currentStack, newContent]);

    return onClose$.asObservable();
  }

  public close(result?: any): void {
    const currentStack = this.componentStack$.getValue();
    if (currentStack.length === 0) {
      return;
    }
    const rootPanel = currentStack[0];
    rootPanel.onClose$.next(result);
    rootPanel.onClose$.complete();

    this.componentStack$.next([]);
  }

  public dismiss(): void {
    const currentStack = this.componentStack$.getValue();
    if (currentStack.length === 0) {
      return;
    }

    const rootPanel = currentStack[0];
    rootPanel.onClose$.complete();

    this.componentStack$.next([]);
  }

  /**
   * Navigates forward by pushing a new component onto the stack.
   */
  public navigateForward<T>(component: Type<T>, data?: any): void {
    const currentStack = this.componentStack$.getValue();
    if (currentStack.length === 0) return;

    const rootPanelOnClose$ = currentStack[0].onClose$;

    const newContent: SidePanelContent<T> = {
      component,
      data,
      onClose$: rootPanelOnClose$,
    };

    this.componentStack$.next([...currentStack, newContent]);
  }

  /**
   * Navigates backward by popping the current component from the stack.
   */
  public navigateBack(): void {
    const currentStack = this.componentStack$.getValue();
    if (currentStack.length <= 1) return;

    this.componentStack$.next(currentStack.slice(0, -1));
  }

  // Expose the stack so the wrapper knows if it can go back.
  public readonly stackSize$: Observable<number> = this.componentStack$.pipe(map(stack => stack.length));
}